import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { resources, getResourceSources } from '../../data/resources';
import { buildings } from '../../data/buildings';

/**
 * ResourceChip - A reusable component for displaying resources
 * Shows tooltip with resource info, sources, and production breakdown on hover
 * 
 * Usage:
 *   <ResourceChip resourceId="wood" amount={100} />
 *   <ResourceChip resourceId="gold" amount={50} showName />
 *   <ResourceChip resourceId="iron" /> // Just icon
 *   <ResourceChip resourceId="wood" amount={100} currentAmount={50} /> // Shows have/need
 *   <ResourceChip resourceId="food" breakdown={getProductionBreakdown('food')} /> // With modifiers
 */
function ResourceChip({ 
  resourceId, 
  amount, 
  currentAmount,  // Optional: player's current amount (for showing have/need)
  showName = false,
  showTooltip = true,
  className = '',
  canAfford = true,  // For cost displays
  size = 'normal',   // 'small', 'normal', 'large'
  breakdown = null,  // Optional: production breakdown from getProductionBreakdown()
  productionRate = null, // Optional: simple rate for display
}) {
  const [tooltip, setTooltip] = useState(null);

  const resource = resources[resourceId];
  
  if (!resource) {
    return <span className="rc-unknown">❓{amount}</span>;
  }

  const handleMouseEnter = (e) => {
    if (!showTooltip) return;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseMove = (e) => {
    if (!tooltip) return;
    setTooltip({
      x: e.clientX,
      y: e.clientY,
    });
  };

  const handleMouseLeave = () => {
    setTooltip(null);
  };

  // Get sources for this resource
  const sources = getResourceSources(resourceId, buildings);

  const sizeClass = size === 'small' ? 'rc-small' : size === 'large' ? 'rc-large' : '';
  // Only show afford styling when it's a cost comparison (currentAmount provided)
  const isCostDisplay = currentAmount !== undefined;
  const affordClass = isCostDisplay ? (canAfford ? 'rc-can-afford' : 'rc-cannot-afford') : '';

  return (
    <>
      <span
        className={`resource-chip ${sizeClass} ${affordClass} ${className}`}
        onMouseEnter={handleMouseEnter}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <span className="rc-icon">{resource.icon}</span>
        {amount !== undefined && <span className="rc-amount">{formatAmount(amount)}</span>}
        {showName && <span className="rc-name">{resource.name}</span>}
      </span>

      {tooltip && ReactDOM.createPortal(
        <ResourceTooltip 
          resource={resource}
          sources={sources}
          amount={amount}
          currentAmount={currentAmount}
          canAfford={canAfford}
          breakdown={breakdown}
          productionRate={productionRate}
          x={tooltip.x}
          y={tooltip.y}
        />,
        document.body
      )}
    </>
  );
}

/**
 * ResourceTooltip - The popup that shows resource info and breakdown
 */
function ResourceTooltip({ resource, sources, amount, currentAmount, canAfford, breakdown, productionRate, x, y }) {
  const tooltipWidth = 300;
  const tooltipHeight = 400; // Conservative estimate
  
  let left = x + 15;
  let top = y - 10;
  
  if (left + tooltipWidth > window.innerWidth - 10) {
    left = x - tooltipWidth - 15;
  }
  if (left < 10) left = 10;
  if (top + tooltipHeight > window.innerHeight - 10) {
    top = window.innerHeight - tooltipHeight - 10;
  }
  if (top < 10) top = 10;

  const showCostComparison = amount !== undefined && currentAmount !== undefined;
  const hasProduction = breakdown?.final > 0 || productionRate > 0;

  return (
    <div 
      className="resource-tooltip"
      style={{ left, top }}
    >
      <div className="rt-header">
        <span className="rt-icon">{resource.icon}</span>
        <div className="rt-info">
          <span className="rt-name">{resource.name}</span>
          <span className="rt-tier">Tier {resource.tier}</span>
        </div>
        {hasProduction && (
          <span className="rt-rate-badge">
            +{formatRate(breakdown?.final || productionRate)}/s
          </span>
        )}
      </div>
      
      {/* Cost comparison */}
      {showCostComparison && (
        <div className={`rt-cost-comparison ${canAfford ? 'can-afford' : 'cannot-afford'}`}>
          <div className="rt-cost-row">
            <span className="rt-cost-label">Have</span>
            <span className="rt-cost-value">{formatAmount(currentAmount)}</span>
          </div>
          <span className="rt-cost-sep">/</span>
          <div className="rt-cost-row">
            <span className="rt-cost-label">Need</span>
            <span className="rt-cost-value">{formatAmount(amount)}</span>
          </div>
          <span className="rt-cost-status">
            {canAfford ? '✓' : `−${formatAmount(amount - currentAmount)}`}
          </span>
        </div>
      )}
      
      <p className="rt-desc">{resource.description}</p>
      
      {/* Production Breakdown */}
      {breakdown && breakdown.base > 0 && (
        <div className="rt-breakdown">
          <div className="rt-breakdown-title">📊 Production Breakdown</div>
          
          {/* Base from buildings */}
          <div className="rt-bd-row base">
            <span className="rt-bd-label">🏠 Buildings</span>
            <span className="rt-bd-value">+{formatRate(breakdown.base)}/s</span>
          </div>
          
          {/* Building details (collapsed) */}
          {breakdown.buildings && breakdown.buildings.length > 0 && (
            <div className="rt-bd-buildings">
              {breakdown.buildings.slice(0, 4).map((b, i) => (
                <div key={i} className="rt-bd-building">
                  <span className="rt-bd-b-name">{b.name} ×{b.count}</span>
                  <span className="rt-bd-b-rate">+{formatRate(b.totalRate)}</span>
                </div>
              ))}
              {breakdown.buildings.length > 4 && (
                <div className="rt-bd-building more">
                  +{breakdown.buildings.length - 4} more...
                </div>
              )}
            </div>
          )}
          
          {/* Advisor bonus */}
          {breakdown.advisorBonus !== 0 && (
            <div className={`rt-bd-row ${breakdown.advisorBonus > 0 ? 'bonus' : 'penalty'}`}>
              <span className="rt-bd-label">👑 Advisors</span>
              <span className="rt-bd-value">
                {breakdown.advisorBonus > 0 ? '+' : ''}{Math.round(breakdown.advisorBonus * 100)}%
              </span>
            </div>
          )}
          
          {/* Advisor penalty (separate if exists) */}
          {breakdown.advisorPenalty !== 0 && (
            <div className="rt-bd-row penalty">
              <span className="rt-bd-label">👑 Advisor Penalty</span>
              <span className="rt-bd-value">
                {Math.round(breakdown.advisorPenalty * 100)}%
              </span>
            </div>
          )}
          
          {/* Wonder bonus */}
          {breakdown.wonderBonus !== 0 && (
            <div className="rt-bd-row bonus">
              <span className="rt-bd-label">🏛️ Wonders</span>
              <span className="rt-bd-value">
                +{Math.round(breakdown.wonderBonus * 100)}%
              </span>
            </div>
          )}
          
          {/* Divider and final */}
          <div className="rt-bd-divider" />
          <div className="rt-bd-row final">
            <span className="rt-bd-label">Total</span>
            <span className="rt-bd-value">+{formatRate(breakdown.final)}/s</span>
          </div>
        </div>
      )}
      
      {/* Simple rate display if no breakdown */}
      {!breakdown && productionRate > 0 && (
        <div className="rt-simple-rate">
          <span>📈 Production:</span>
          <span className="rt-sr-value">+{formatRate(productionRate)}/s</span>
        </div>
      )}
      
      {/* Sources */}
      {sources.length > 0 && !breakdown && (
        <div className="rt-sources">
          <div className="rt-sources-title">🔍 Sources:</div>
          <div className="rt-sources-list">
            {sources.slice(0, 5).map((source, i) => (
              <div key={i} className="rt-source">
                <span className="rt-source-icon">{source.icon}</span>
                <span className="rt-source-text">{source.text}</span>
              </div>
            ))}
            {sources.length > 5 && (
              <div className="rt-source rt-more">
                +{sources.length - 5} more sources...
              </div>
            )}
          </div>
        </div>
      )}
      
      {sources.length === 0 && !breakdown && resource.unlockHint && (
        <div className="rt-unlock-hint">
          <span className="rt-hint-icon">💡</span>
          <span>{resource.unlockHint}</span>
        </div>
      )}
    </div>
  );
}

/**
 * Format large numbers nicely
 */
function formatAmount(num) {
  if (num === undefined || num === null) return '';
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 10000) return (num / 1000).toFixed(0) + 'K';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return Math.floor(num).toString();
}

/**
 * Format rate numbers
 */
function formatRate(rate) {
  if (rate === undefined || rate === null) return '0';
  if (rate >= 100) return Math.floor(rate).toString();
  if (rate >= 10) return rate.toFixed(1);
  if (rate >= 1) return rate.toFixed(2);
  return rate.toFixed(3);
}

/**
 * ResourceCost - Convenience wrapper for displaying costs
 */
export function ResourceCost({ resourceId, amount, available = 0 }) {
  const canAfford = available >= amount;
  return (
    <ResourceChip 
      resourceId={resourceId} 
      amount={amount} 
      canAfford={canAfford}
      size="small"
    />
  );
}

/**
 * ResourceList - Display multiple resources
 */
export function ResourceList({ resources: resList, playerResources = {}, showTooltips = true }) {
  return (
    <div className="resource-list">
      {Object.entries(resList).map(([resId, amount]) => (
        <ResourceChip
          key={resId}
          resourceId={resId}
          amount={amount}
          canAfford={(playerResources[resId] || 0) >= amount}
          showTooltip={showTooltips}
          size="small"
        />
      ))}
    </div>
  );
}

/**
 * ResourceDisplay - For showing current resources with breakdown
 * Used in resource bars/headers
 */
export function ResourceDisplay({ 
  resourceId, 
  amount, 
  breakdown,
  showRate = true,
  size = 'normal',
}) {
  const resource = resources[resourceId];
  if (!resource) return null;
  
  const rate = breakdown?.final || 0;
  
  return (
    <ResourceChip
      resourceId={resourceId}
      amount={amount}
      breakdown={breakdown}
      productionRate={rate}
      size={size}
      showName={size === 'large'}
    />
  );
}

export default ResourceChip;
